import { useEffect, useState } from "react";
import type {
  StudentBillingPayerOverviewChargeDto,
  StudentBillingPayerOverviewDto,
  StudentBillingPaymentExecutionStatus,
  StudentBillingPaymentSetupStatus
} from "@madamuh/domain";
import { useLocation, useParams } from "react-router-dom";
import { fetchStudentBillingPayerOverview } from "../../shared/api/studentBillingPayerOverview";
import { beginOwnStudentBillingPaymentSetup } from "../../shared/api/studentBillingPaymentSetup";
import { getProtectedApiErrorMessage } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { FamilyAppShell } from "../../shared/ui/family/FamilyAppShell";
import { formatFamilyDate, formatFamilyDateTime } from "./familyUi";

export function studentBillingPaymentStatusLabel(status: StudentBillingPaymentExecutionStatus): string {
  if (status === "paid") return "支払完了";
  if (status === "invoicePending" || status === "paymentPending" || status === "processing") return "処理中";
  if (status === "retryPending") return "クラブ側で確認中";
  if (status === "cancelled") return "キャンセル";
  return "支払いを確認できません";
}

export function studentBillingSetupStatusLabel(status: StudentBillingPaymentSetupStatus): string {
  if (status === "notStarted") return "未登録";
  if (status === "ready") return "登録済み";
  if (status === "creating" || status === "checkoutCreated") return "登録手続き中";
  if (status === "processing") return "確認中";
  if (status === "failed") return "登録結果を確認できません";
  if (status === "expired") return "登録手続きの期限切れ";
  return "登録状態を確認できません";
}

function serviceMonthLabel(value: string | null): string {
  if (!value) return "対象月未設定";
  const [year, month] = value.split("-");
  return `${Number(year)}年${Number(month)}月分`;
}

function chargeTitle(charge: StudentBillingPayerOverviewChargeDto): string {
  return charge.chargeType === "regularMonthly"
    ? `${serviceMonthLabel(charge.serviceMonth)} 月謝`
    : "単発請求";
}

function failureGuidance(charge: StudentBillingPayerOverviewChargeDto): string | null {
  if (charge.safeFailureCode === "PAYER_ACTION_REQUIRED") {
    return "カード会社で追加確認が必要です。クラブへお問い合わせください。";
  }
  if (charge.safeFailureCode === "PAYMENT_METHOD_DECLINED") {
    return "お支払いを確認できませんでした。登録カードをご確認いただくか、クラブへお問い合わせください。";
  }
  if (charge.safeFailureCode === "PAYMENT_CANCELLED") {
    return "お支払いはキャンセルされました。必要な対応についてクラブへお問い合わせください。";
  }
  if (charge.canRetry) return "クラブ側で確認中です。支払担当者から再実行する必要はありません。";
  return null;
}

function PaymentMethodSection({
  state,
  loading,
  cancelled,
  onBeginSetup,
  onRefresh
}: {
  state: NonNullable<Extract<StudentBillingPayerOverviewDto, { access: "payer" }>["paymentMethod"]>;
  loading: boolean;
  cancelled: boolean;
  onBeginSetup: () => void;
  onRefresh: () => void;
}) {
  const setupBusy = state.setupStatus === "creating" || state.setupStatus === "processing";
  const actionLabel = state.paymentMethodReady
    ? "カードを変更"
    : state.checkoutAvailable
      ? "カード登録を続ける"
      : "カードを登録";
  return (
    <section className="family-surface family-section student-billing-section">
      <div className="student-billing-section__heading">
        <div>
          <p className="family-list-card__eyebrow">支払い方法</p>
          <h3 className="student-billing-section__title">登録カード</h3>
        </div>
        <span className={`student-billing-status student-billing-status--${state.paymentMethodReady ? "ready" : "neutral"}`}>
          {studentBillingSetupStatusLabel(state.setupStatus)}
        </span>
      </div>
      {cancelled ? (
        <div className="student-billing-guidance" role="status">
          カード登録を中止しました。登録済みのカードがある場合、そのカードは変更されていません。
        </div>
      ) : null}
      {!state.accountReady ? (
        <p className="family-text-muted">クラブの決済設定が完了していないため、現在カード登録はできません。</p>
      ) : state.paymentMethodReady && state.card ? (
        <div className="student-billing-card-summary">
          <strong>{state.card.brand} •••• {state.card.last4}</strong>
          <span>有効期限 {String(state.card.expiryMonth).padStart(2, "0")}/{state.card.expiryYear}</span>
        </div>
      ) : (
        <p className="family-text-muted">支払いカードはまだ登録されていません。</p>
      )}
      {state.setupStatus === "processing" ? (
        <div className="student-billing-guidance" role="status">
          Stripeから登録結果を確認しています。ブラウザから戻っただけでは登録完了になりません。少し待って状態を更新してください。
        </div>
      ) : null}
      {state.setupStatus === "failed" || state.setupStatus === "expired" ? (
        <div className="student-billing-guidance student-billing-guidance--warning">
          カード登録を完了できませんでした。安全なStripe画面からもう一度お試しください。
        </div>
      ) : null}
      {!state.canManagePaymentMethod ? (
        <p className="family-text-muted">カードの登録・変更は現在利用できません。</p>
      ) : null}
      <div className="student-billing-actions">
        {state.canManagePaymentMethod ? (
          <button
            type="button"
            className="family-primary-button"
            disabled={loading || !state.accountReady || setupBusy}
            onClick={onBeginSetup}
          >
            {actionLabel}
          </button>
        ) : null}
        <button type="button" className="family-secondary-button" disabled={loading} onClick={onRefresh}>
          状態を更新
        </button>
      </div>
    </section>
  );
}

export function StudentBillingPaymentMethodPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const location = useLocation();
  const { routeStatus, sessionStatus, session, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [overview, setOverview] = useState<StudentBillingPayerOverviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setMessage(null);
    try { setOverview(await fetchStudentBillingPayerOverview(tenantSlug)); }
    catch (error) { setMessage(getProtectedApiErrorMessage(error, "お支払い情報を確認できませんでした。")); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (isSessionReady) void load(); }, [isSessionReady, tenantSlug]);

  async function beginSetup() {
    setLoading(true);
    setMessage(null);
    try {
      const setup = await beginOwnStudentBillingPaymentSetup(tenantSlug);
      window.location.assign(setup.checkoutUrl);
    } catch (error) {
      setMessage(getProtectedApiErrorMessage(error, "カード登録を開始できませんでした。"));
      setLoading(false);
    }
  }

  const payer = overview?.access === "payer" ? overview : null;
  return (
    <FamilyAppShell tenantSlug={tenantSlug} tenantName={session?.tenant.displayName} session={session} activeTab="home" navigationCountsEnabled={false}>
      <div className="family-page-stack">
        <header>
          <p className="family-page-intro">生徒・保護者向け請求</p>
          <h2 className="family-page-title">お支払い</h2>
          <p className="family-text-muted">登録カードと、担当している生徒のお支払い状況を確認できます。</p>
        </header>
        {routeStatus === "checking" || sessionStatus === "loading" || loading && !overview ? (
          <section className="family-surface family-section"><p className="family-text-muted">確認しています。</p></section>
        ) : null}
        {message ? <section className="family-surface family-section"><p role="alert">{message}</p></section> : null}
        {overview?.access === "notPayer" ? (
          <section className="family-surface family-section student-billing-non-payer">
            <h3 className="student-billing-section__title">支払担当者ではありません</h3>
            <p className="family-text-muted">このアカウントは、生徒向け請求の支払担当者として登録されていません。</p>
          </section>
        ) : null}
        {payer ? (
          <>
            <section className="family-surface family-section student-billing-section">
              <p className="family-list-card__eyebrow">支払担当</p>
              <h3 className="student-billing-section__title">現在お支払いを担当している生徒</h3>
              {payer.assignedStudents.length === 0 ? (
                <p className="family-text-muted">現在のお支払い担当はありません。過去のお支払い履歴は引き続き確認できます。</p>
              ) : (
                <div className="student-billing-students">
                  {payer.assignedStudents.map((student) => (
                    <span key={student.studentId}>
                      {student.displayName}（{student.collectionMethod === "card" ? "カード決済" : "テナント徴収"}）
                    </span>
                  ))}
                </div>
              )}
            </section>
            {payer.paymentMethod ? (
              <PaymentMethodSection
                state={payer.paymentMethod}
                loading={loading}
                cancelled={location.pathname.endsWith("/cancel")}
                onBeginSetup={() => void beginSetup()}
                onRefresh={() => void load()}
              />
            ) : null}
            <section className="family-surface family-section student-billing-section">
              <div>
                <p className="family-list-card__eyebrow">最大20件</p>
                <h3 className="student-billing-section__title">お支払い履歴</h3>
                <p className="family-text-muted">通常月謝は毎月26日に翌月分の決済を開始します。入金日はクラブによって異なります。</p>
              </div>
              {payer.customerInvoices.length === 0 && payer.charges.length === 0 ? <p className="family-empty-state">お支払い履歴はありません。</p> : (
                <div className="student-billing-history">
                  {payer.customerInvoices.map((invoice) => (
                    <article key={invoice.customerInvoiceId} className="student-billing-charge-card">
                      <div className="student-billing-charge-card__heading">
                        <div><p className="family-list-card__eyebrow">{invoice.serviceMonth.replace("-", "年")}月分</p><h4>月次カード請求（{invoice.billingUnitCount}件）</h4></div>
                        <span className={`student-billing-status student-billing-status--${invoice.status === "paid" ? "ready" : invoice.status === "failed" || invoice.status === "needsAttention" ? "warning" : "neutral"}`}>{invoice.status === "paid" ? "支払完了" : invoice.status === "failed" ? "決済失敗" : invoice.status === "needsAttention" ? "確認が必要" : invoice.status === "processing" || invoice.status === "paymentPending" ? "決済処理中" : "請求準備済み"}</span>
                      </div>
                      <strong className="student-billing-charge-card__amount">{invoice.grossAmount.toLocaleString("ja-JP")}円</strong>
                      <dl className="student-billing-charge-card__details">
                        <div><dt>決済予定日</dt><dd>{formatFamilyDate(invoice.scheduledChargeDate)}</dd></div>
                        <div><dt>決済試行日時</dt><dd>{invoice.attemptedAt ? formatFamilyDateTime(invoice.attemptedAt) : "未実行"}</dd></div>
                        <div><dt>支払完了日時</dt><dd>{invoice.paidAt ? formatFamilyDateTime(invoice.paidAt) : "未完了"}</dd></div>
                      </dl>
                      {invoice.safeFailureCode ? <p className="student-billing-guidance student-billing-guidance--warning">カード情報をご確認ください。必要に応じてクラブへお問い合わせください。</p> : null}
                    </article>
                  ))}
                  {payer.charges.map((charge) => {
                    const guidance = failureGuidance(charge);
                    return (
                      <article key={charge.executionId} className="student-billing-charge-card">
                        <div className="student-billing-charge-card__heading">
                          <div>
                            <p className="family-list-card__eyebrow">{charge.studentDisplayName}</p>
                            <h4>{chargeTitle(charge)}</h4>
                          </div>
                          <span className={`student-billing-status student-billing-status--${charge.status === "paid" ? "ready" : charge.status === "failed" || charge.status === "cancelled" ? "warning" : "neutral"}`}>
                            {studentBillingPaymentStatusLabel(charge.status)}
                          </span>
                        </div>
                        <strong className="student-billing-charge-card__amount">{charge.amount.toLocaleString("ja-JP")}円</strong>
                        <dl className="student-billing-charge-card__details">
                          <div><dt>決済予定日</dt><dd>{formatFamilyDate(charge.scheduledChargeDate)}</dd></div>
                          <div><dt>決済試行日時</dt><dd>{charge.attemptedAt ? formatFamilyDateTime(charge.attemptedAt) : "未実行"}</dd></div>
                          <div><dt>支払完了日時</dt><dd>{charge.paidAt ? formatFamilyDateTime(charge.paidAt) : "未完了"}</dd></div>
                        </dl>
                        {guidance ? <p className="student-billing-guidance student-billing-guidance--warning">{guidance}</p> : null}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}
